import { Grid, LinearProgress } from "@mui/material";
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  KpiCard,
} from "../../../components";
import { formatPrice } from "../../../utils";

const ANEXO_I = [
  { faixa: "1ª Faixa", min: 0, max: 180000, nominal: "4,00%", deducao: "R$ 0" },
  {
    faixa: "2ª Faixa",
    min: 180000,
    max: 360000,
    nominal: "7,30%",
    deducao: "R$ 5.940",
  },
  {
    faixa: "3ª Faixa",
    min: 360000,
    max: 720000,
    nominal: "9,50%",
    deducao: "R$ 13.860",
  },
  {
    faixa: "4ª Faixa",
    min: 720000,
    max: 1800000,
    nominal: "10,70%",
    deducao: "R$ 22.500",
  },
  {
    faixa: "5ª Faixa",
    min: 1800000,
    max: 3600000,
    nominal: "14,30%",
    deducao: "R$ 87.300",
  },
  {
    faixa: "6ª Faixa",
    min: 3600000,
    max: 4800000,
    nominal: "19,00%",
    deducao: "R$ 378.000",
  },
];

function findFaixa(rbt12 = 0) {
  if (!rbt12) return null;
  const v = Number(rbt12) || 0;
  return ANEXO_I.findIndex((f) => v > f.min && v <= f.max);
}

export default function PainelAnaliseFiscalEstimativaTributos({
  resumoFiscal = {},
  loading = false,
}) {
  const faixaAtiva = findFaixa(resumoFiscal.rbt12 || 0);
  const faturamentoMes = resumoFiscal.faturamentoNF ?? 0;
  const faixa = resumoFiscal.faixaAnexo ?? null;
  const aliquotaEfetiva = (() => {
    if (resumoFiscal.aliquotaEfetiva != null)
      return Number(resumoFiscal.aliquotaEfetiva) || 0;
    if (faixa && resumoFiscal.rbt12) {
      const nominal =
        Number(String(faixa.nominal).replace("%", "").replace(",", ".")) || 0;
      const deducao = Number(faixa.deducao) || 0;
      const rbt12 = Number(resumoFiscal.rbt12) || 0;
      return rbt12 ? ((rbt12 * (nominal / 100) - deducao) / rbt12) * 100 : 0;
    }
    return 0;
  })();

  const percentS =
    faturamentoMes > 0 ? (faturamentoMes / faturamentoMes) * 100 : 0;
  const faturamentoComSubstituicao =
    resumoFiscal.faturamentoComSubstituicao ?? 0;
  const percentC =
    faturamentoMes > 0
      ? (faturamentoComSubstituicao / faturamentoMes) * 100
      : 0;

  return (
    <Grid container spacing={2} className="mt-2">
      <Grid item xs={12}>
        <Box display="flex" alignItems="center" gap={1.5} className="mb-3">
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: 2,
              backgroundColor: "#1976d218",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <i
              className="ph-bold ph-percent"
              style={{ color: "#1976d2", fontSize: 22 }}
            />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, lineHeight: 1.1, color: "#1e293b" }}
            >
              Imposto do mês
            </Typography>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                color: "text.disabled",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              Apuração do Simples Nacional — Anexo I (comércio)
            </Typography>
          </Box>
        </Box>
        <Grid container spacing={2}>
          {[
            {
              title: "Alíquota Efetiva",
              value: `${Number(aliquotaEfetiva ?? 0).toFixed(2)}%`,
              icon: "ph-bold ph-percent",
              color: "#1976d2",
            },
            {
              title: "Faturamento do Mês",
              value: formatPrice(faturamentoMes),
              icon: "ph-bold ph-receipt",
              color: "#1976d2",
            },
            {
              title: "Valor a Pagar",
              value: formatPrice(
                (faturamentoMes * (aliquotaEfetiva ?? 0)) / 100 || 0,
              ),
              icon: "ph-bold ph-coins",
              color: "#f7c948",
            },
            {
              title: "RBT12 (últimos 12 meses)",
              value: formatPrice(resumoFiscal.rbt12 ?? 0),
              icon: "ph-bold ph-calendar",
              color: "#8e24aa",
            },
          ].map((card, i) => (
            <Grid item xs={6} md={3} key={i}>
              <KpiCard
                title={card.title}
                value={card.value}
                icon={card.icon}
                color={card.color}
                loading={loading}
              />
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={8}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Composição do faturamento
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      Participação com e sem substituição tributária
                    </Typography>
                  </Grid>
                  <Grid item xs={4} textAlign="right">
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {formatPrice(faturamentoMes)}
                    </Typography>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Faturamento s/ Substituição
                  </Typography>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={2}
                    sx={{ mt: 0.5 }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={percentS}
                        sx={{ height: 10, borderRadius: 2 }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {formatPrice(faturamentoMes)}
                    </Typography>
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", mt: 1 }}
                  >
                    Faturamento c/ Substituição
                  </Typography>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={2}
                    sx={{ mt: 0.5 }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={percentC}
                        sx={{ height: 10, borderRadius: 2 }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {formatPrice(faturamentoComSubstituicao)}
                    </Typography>
                  </Box>

                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      Faturamento do Mês
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      {formatPrice(faturamentoMes)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 2, height: "100%" }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Tabela Anexo I
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", display: "block", mb: 1 }}
                >
                  Faixa aplicada nesta competência
                </Typography>
                <Table size="small">
                  <TableHead sx={{ backgroundColor: "#f8fafc" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Faixa</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        Receita 12M
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Nominal</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Dedução</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ANEXO_I.map((f, idx) => {
                      const active = idx === faixaAtiva;
                      return (
                        <TableRow
                          key={f.faixa}
                          sx={active ? { backgroundColor: "#e6f4ff" } : {}}
                        >
                          <TableCell sx={{ fontWeight: active ? 700 : 500 }}>
                            {f.faixa}
                          </TableCell>
                          <TableCell>
                            {f.min === 0
                              ? `Até ${formatPrice(f.max)}`
                              : `${formatPrice(f.min)} – ${formatPrice(f.max)}`}
                          </TableCell>
                          <TableCell>{f.nominal}</TableCell>
                          <TableCell>{f.deducao}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}
